import React, { useEffect, useRef, useState } from 'react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import './AlienCharacter.css';

export type AlienBehavior = 'hidden' | 'peek' | 'walk' | 'sit' | 'wave' | 'scurry';

interface AlienCharacterProps {
  behavior: AlienBehavior;
  onAnimationComplete?: () => void;
  position?: { x: number; y: number };
}

// Import animations
const animations: Record<string, any> = {};

const loadAnimation = async (name: string) => {
  if (!animations[name]) {
    try {
      const response = await fetch(`/animations/aliens/${name}.json`);
      animations[name] = await response.json();
    } catch (error) {
      console.error(`Failed to load animation: ${name}`, error);
    }
  }
  return animations[name];
};

const AlienCharacter: React.FC<AlienCharacterProps> = ({
  behavior,
  onAnimationComplete,
  position,
}) => {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [animationData, setAnimationData] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (behavior === 'hidden') {
      setIsVisible(false);
      setAnimationData(null);
      return;
    }

    const loadAndPlay = async () => {
      const animName = behavior === 'scurry' ? 'scurry' : behavior;
      const data = await loadAnimation(animName);
      setAnimationData(data);
      setIsVisible(true);
    };

    loadAndPlay();
  }, [behavior]);

  const handleComplete = () => {
    if (behavior === 'scurry') {
      setIsVisible(false);
    }
    onAnimationComplete?.();
  };

  if (!isVisible || !animationData) {
    return null;
  }

  const getBehaviorClass = () => {
    switch (behavior) {
      case 'peek': return 'alien-peek';
      case 'walk': return 'alien-walk';
      case 'sit': return 'alien-sit';
      case 'wave': return 'alien-wave';
      case 'scurry': return 'alien-scurry';
      default: return '';
    }
  };

  const style: React.CSSProperties = position
    ? { left: position.x, top: position.y }
    : {};

  return (
    <div
      className={`alien-character ${getBehaviorClass()}`}
      style={style}
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        loop={behavior !== 'scurry' && behavior !== 'peek'}
        autoplay
        onComplete={handleComplete}
        style={{ width: 100, height: 100 }}
      />
    </div>
  );
};

export default AlienCharacter;
